import AppLayout from '@amzn/awsui-components-react/polaris/app-layout';
import BreadcrumbGroup from '@amzn/awsui-components-react/polaris/breadcrumb-group';
import Flashbar from '@amzn/awsui-components-react/polaris/flashbar';
import {
    AuthTokenExceptionCopyText,
    AuthTokenExceptionModal,
    AuthTokenExceptions,
    Constants,
    Cookies,
    ErrorBoundary,
    EventEmitter,
    Footer as AppFooter,
    LogLevel,
    useLogger,
    useScrollPadding,
} from '@amzn/event-engine-js-utils';
import { state as breadcrumbsState } from 'atoms/breadcrumbs';
import { useRecoilContentTypeState } from 'atoms/content-type';
import { state as disableContentPaddingsState } from 'atoms/disable-content-paddings';
import { state as maxContentWidthState } from 'atoms/max-content-width';
import { state as navigationContentState } from 'atoms/navigation-content';
import { state as notificationsState } from 'atoms/notifications';
import { state as toolsState } from 'atoms/tools';
import ErrorPageController from 'components/ErrorPageController';
import { useNotification } from 'hooks';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import AppRoutes from '../AppRoutes';
import AppTopNav from '../AppTopNav';

const cookies = Cookies.getInstance();
const componentIds = {
    header: 'app-header',
    footer: 'app-footer',
    notifications: 'app-notifications',
};

export const App = () => {
    const eventEmitter = useRef(EventEmitter.getInstance());
    const { updateScrollPadding } = useScrollPadding();
    const [
        authTokenExceptionType,
        setAuthTokenExceptionType,
    ] = useState<AuthTokenExceptions>();
    const [hideNavigation, setHideNavigation] = useState<boolean>(true);
    const history = useHistory();
    const [errorBoundaryLogger] = useLogger(
        'ErrorBoundaryEEEventPortalApp',
        LogLevel.ERROR
    );
    const navigateBreadcrumb = (event: CustomEvent<any>) => {
        event.preventDefault();
        if (!event.detail.href) {
            return;
        }
        history.push(event.detail.href);
    };

    const appNavContent = useRecoilValue(navigationContentState);
    const breadcrumbs = useRecoilValue(breadcrumbsState);
    const [tools, setTools] = useRecoilState(toolsState);
    const [contentType, setContentType] = useRecoilContentTypeState();
    const notifications = useRecoilValue(notificationsState);
    const disableContentPaddings = useRecoilValue(disableContentPaddingsState);
    const maxContentWidth = useRecoilValue(maxContentWidthState);
    const { showNotification } = useNotification();

    useEffect(() => {
        if (appNavContent) {
            setHideNavigation(false);
        } else {
            setHideNavigation(true);
        }
    }, [appNavContent]);

    useEffect(() => {
        updateScrollPadding();
    }, [notifications]);

    useEffect(() => {
        // when the main bundle.js has been downloaded, remove the raw loader
        // from the view.
        const rawLoader = document.getElementById('raw-loader-container');
        rawLoader?.classList?.add('hide-raw-loader-container');

        eventEmitter.current.on(
            Constants.EVENTS.AMAZON_CORPORATE_SSO_TOKEN,
            () => {
                setAuthTokenExceptionType('AmazonCorporateSSOToken');
                showNotification(
                    AuthTokenExceptionCopyText.AmazonCorporateSSOToken.body,
                    { type: 'warning' }
                );
            }
        );

        eventEmitter.current.on(Constants.EVENTS.NETWORK_ERROR, () => {
            setAuthTokenExceptionType('NetworkError');
            showNotification(AuthTokenExceptionCopyText.NetworkError.body, {
                type: 'warning',
            });
        });

        // check for cookie consent once app loads
        cookies.checkForConsent();
    }, []);

    return (
        <>
            <AppTopNav id={componentIds.header} />
            <AppLayout
                navigation={appNavContent}
                maxContentWidth={maxContentWidth}
                breadcrumbs={
                    breadcrumbs?.length ? (
                        <BreadcrumbGroup
                            items={breadcrumbs}
                            onClick={navigateBreadcrumb}
                        />
                    ) : null
                }
                headerSelector={`#${componentIds.header}`}
                footerSelector={`#${componentIds.footer}`}
                content={
                    <ErrorBoundary
                        logger={errorBoundaryLogger}
                        fallback={<ErrorPageController type="catastrophic" />}>
                        <>
                            {authTokenExceptionType && (
                                <AuthTokenExceptionModal
                                    type={authTokenExceptionType}
                                />
                            )}
                            <AppRoutes />
                        </>
                    </ErrorBoundary>
                }
                tools={tools.content}
                toolsOpen={tools.open}
                toolsHide={tools.hide}
                onToolsChange={({ detail: { open } }) =>
                    setTools({ ...tools, open })
                }
                notifications={
                    notifications?.length ? (
                        <div id={componentIds.notifications}>
                            <Flashbar items={notifications} />
                        </div>
                    ) : null
                }
                navigationHide={hideNavigation}
                disableContentPaddings={disableContentPaddings}
                contentType={contentType}
                stickyNotifications
            />
            <AppFooter id={componentIds.footer} sticky />
        </>
    );
};
